import React, {useState, useMemo} from 'react'
import {Col, Row, Block} from 'jsxstyle'
import Loader from '../../components/Loader'
import {useXpertKnowledgeBufferContext, XpertKnowledgeBufferContextProvider} from './XpertKnowledgeBufferContext'
import {useXpertKnowledgeAuthContext} from '../../components/AuthContext/XpertKnowledgeAuthContext'
import {ReactComponent as DeleteIcon} from '../../common/assets/delete-icon.svg'
import XpertKnowledgeBufferRow from './components/XpertKnowledgeBufferRow'
import i18n from '../../common/i18n'
import CustomDropdown from '../../components/CustomDropdown'

type SortField = 'createdAt' | 'reference' | 'status'
type SortOrder = 'ASC' | 'DESC'

interface FilterState {
  transcriptStatus: string
  recordingStatus: string
  apiCallStatus: string
  xpertKnowledgeStatus: string
  isHidden: string
}

const XpertKnowledgeBuffer: React.FC = () => {
  const {isLoading, xpertKnowledgeBuffers} = useXpertKnowledgeBufferContext()
  const {user} = useXpertKnowledgeAuthContext()

  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortOrder, setSortOrder] = useState<SortOrder>('DESC')

  const [filters, setFilters] = useState<FilterState>({
    transcriptStatus: '',
    recordingStatus: '',
    apiCallStatus: '',
    xpertKnowledgeStatus: '',
    isHidden: '',
  })

  const booleanOptions = ['', 'Yes', 'No']
  const sortFieldOptions = [
    {value: 'createdAt', label: 'Created At'},
    {value: 'reference', label: 'Reference'},
    {value: 'status', label: 'Status'},
  ]
  const sortOrderOptions = ['ASC', 'DESC']

  const filteredAndSortedBuffers = useMemo(() => {
    let filtered = [...xpertKnowledgeBuffers]

    if (filters.transcriptStatus) {
      const filterValue = filters.transcriptStatus === 'Yes'
      filtered = filtered.filter((buffer) => buffer.transcriptStatus === filterValue)
    }

    if (filters.recordingStatus) {
      const filterValue = filters.recordingStatus === 'Yes'
      filtered = filtered.filter((buffer) => buffer.recordingStatus === filterValue)
    }

    if (filters.apiCallStatus) {
      const filterValue = filters.apiCallStatus === 'Yes'
      filtered = filtered.filter((buffer) => buffer.apiCallStatus === filterValue)
    }

    if (filters.xpertKnowledgeStatus) {
      filtered = filtered.filter((buffer) => buffer.xpertKnowledgeStatus === filters.xpertKnowledgeStatus)
    }

    if (filters.isHidden) {
      const filterValue = filters.isHidden === 'Yes'
      filtered = filtered.filter((buffer) => buffer.isHidden === filterValue)
    }

    filtered.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
        case 'reference':
          comparison = a.reference.localeCompare(b.reference)
          break
        case 'status':
          comparison = a.xpertKnowledgeStatus.localeCompare(b.xpertKnowledgeStatus)
          break
        default:
          comparison = 0
      }

      return sortOrder === 'ASC' ? comparison : -comparison
    })

    return filtered
  }, [xpertKnowledgeBuffers, filters, sortField, sortOrder])

  const handleFilterChange = (filterKey: keyof FilterState, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }))
  }

  const clearAllFilters = () => {
    setFilters({
      transcriptStatus: '',
      recordingStatus: '',
      apiCallStatus: '',
      xpertKnowledgeStatus: '',
      isHidden: '',
    })
  }

  if (user && user.role !== 'ADMIN_SIGHTCALL') {
    return (
      <Col height="100vh" justifyContent="center" alignItems="center" gap="16px" backgroundColor="#f8d7da" padding="20px">
        <Block>
          <DeleteIcon width="80px" height="80px" fill="#721c24" />
        </Block>
        <Block fontSize="32px" fontWeight="bold" color="#721c24">
          {i18n.t('xk.tutorial.buffer.unauthorized')}
        </Block>
      </Col>
    )
  }

  if (isLoading) {
    return (
      <Col width="100vw" height="100vh" display="flex" justifyContent="center" alignItems="center">
        <Col gap="10px" justifyContent="center" alignItems="center" textAlign="center">
          <Loader size={75} />
          <Block fontWeight={500} lineHeight="60px" fontSize="34px">
            {i18n.t('xk.tutorial.buffer.loading')}
          </Block>
        </Col>
      </Col>
    )
  }

  const FilterBar = (
    <Col gap="16px" padding="16px 20px" backgroundColor="#F8F9FA" borderRadius="8px" marginBottom="16px">
      <Row alignItems="center" gap="16px" flexWrap="wrap">
        <Block fontSize="16px" fontWeight={600} color="#333">
          Filters & Sorting:
        </Block>

        <Row alignItems="center" gap="8px">
          <Block fontSize="14px" color="#666">
            Sort by:
          </Block>
          <CustomDropdown
            options={sortFieldOptions.map((opt) => opt.label)}
            value={sortFieldOptions.find((opt) => opt.value === sortField)?.label || ''}
            onChange={(value) => {
              const field = sortFieldOptions.find((opt) => opt.label === value)?.value as SortField
              if (field) setSortField(field)
            }}
            width="140px"
            placeholder="Sort field"
          />
          <CustomDropdown
            options={sortOrderOptions}
            value={sortOrder}
            onChange={(value) => setSortOrder(value as SortOrder)}
            width="100px"
            placeholder="Order"
          />
        </Row>
      </Row>

      <Row alignItems="center" gap="16px" flexWrap="wrap">
        <Block fontSize="14px" color="#666">
          Filter by:
        </Block>

        <Row alignItems="center" gap="8px">
          <Block fontSize="13px" color="#555">
            Transcript:
          </Block>
          <CustomDropdown
            options={booleanOptions}
            value={filters.transcriptStatus}
            onChange={(value) => handleFilterChange('transcriptStatus', value)}
            width="80px"
            placeholder="Any"
          />
        </Row>

        <Row alignItems="center" gap="8px">
          <Block fontSize="13px" color="#555">
            Recording:
          </Block>
          <CustomDropdown
            options={booleanOptions}
            value={filters.recordingStatus}
            onChange={(value) => handleFilterChange('recordingStatus', value)}
            width="80px"
            placeholder="Any"
          />
        </Row>

        <Row alignItems="center" gap="8px">
          <Block fontSize="13px" color="#555">
            API Call:
          </Block>
          <CustomDropdown
            options={booleanOptions}
            value={filters.apiCallStatus}
            onChange={(value) => handleFilterChange('apiCallStatus', value)}
            width="80px"
            placeholder="Any"
          />
        </Row>

        <Row alignItems="center" gap="8px">
          <Block fontSize="13px" color="#555">
            Hidden:
          </Block>
          <CustomDropdown
            options={booleanOptions}
            value={filters.isHidden}
            onChange={(value) => handleFilterChange('isHidden', value)}
            width="80px"
            placeholder="Any"
          />
        </Row>

        <Block fontSize="13px" color="#007bff" cursor="pointer" textDecoration="underline" props={{onClick: clearAllFilters}}>
          Clear All
        </Block>
      </Row>

      <Row alignItems="center">
        <Block fontSize="14px" color="#666">
          Showing {filteredAndSortedBuffers.length} of {xpertKnowledgeBuffers.length} records
        </Block>
      </Row>
    </Col>
  )

  const Header = (
    <Row
      display="grid"
      gridTemplateColumns="2fr 1fr 1fr 1fr 1fr 1fr auto"
      columnGap="16px"
      padding="12px 20px"
      borderRadius="8px"
      backgroundColor="#F0F0F0"
      fontWeight={500}
      fontSize="14px"
    >
      <Block>{i18n.t('xk.tutorial.buffer.reference')}</Block>
      <Block>{i18n.t('xk.tutorial.buffer.createdAt')}</Block>
      <Block justifySelf="center">{i18n.t('xk.tutorial.buffer.transcript')}</Block>
      <Block justifySelf="center">{i18n.t('xk.tutorial.buffer.recording')}</Block>
      <Block justifySelf="center">{i18n.t('xk.tutorial.buffer.apiCall')}</Block>
      <Block justifySelf="center">{i18n.t('xk.tutorial.buffer.status')}</Block>
      <Block justifySelf="center">{i18n.t('xk.tutorial.buffer.hidden')}</Block>
    </Row>
  )

  return (
    <Col padding="20px" backgroundColor="#FFFFFF" height="95%" display="flex" flexDirection="column" gap="10px">
      {FilterBar}
      {Header}
      <Col overflowY="scroll" height="100%" flexGrow={1} gap="10px">
        {filteredAndSortedBuffers.length ? (
          filteredAndSortedBuffers.map((buffer) => <XpertKnowledgeBufferRow key={buffer.id} buffer={buffer} onClick={() => {}} />)
        ) : (
          <Col justifyContent="center" alignItems="center" gap="20px" padding="50px">
            <Block fontSize="24px" fontWeight={500}>
              {xpertKnowledgeBuffers.length === 0 ? i18n.t('xk.tutorial.buffer.empty') : 'No records match the current filters'}
            </Block>
            {xpertKnowledgeBuffers.length > 0 && (
              <Block fontSize="14px" color="#007bff" cursor="pointer" textDecoration="underline" props={{onClick: clearAllFilters}}>
                Clear all filters
              </Block>
            )}
          </Col>
        )}
      </Col>
    </Col>
  )
}

export default () => (
  <XpertKnowledgeBufferContextProvider>
    <XpertKnowledgeBuffer />
  </XpertKnowledgeBufferContextProvider>
)
